import { useState, useCallback } from "react";

const RATE_LIMIT_STORAGE_PREFIX = "rate_limit_";

interface RateLimitConfig {
  key: string;
  maxAttempts: number;
  windowMs: number;
}

interface RateLimitState {
  isLimited: boolean;
  remainingAttempts: number;
  resetTime: number | null;
  timeUntilReset: number;
}

interface UseRateLimitReturn extends RateLimitState {
  checkLimit: () => boolean;
  recordAttempt: () => void;
  reset: () => void;
}

interface StoredAttempts {
  attempts: number[];
}

export function useRateLimit({
  key,
  maxAttempts,
  windowMs,
}: RateLimitConfig): UseRateLimitReturn {
  const storageKey = `${RATE_LIMIT_STORAGE_PREFIX}${key}`;

  const getStoredAttempts = useCallback((): number[] => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return [];
      const parsed: StoredAttempts = JSON.parse(stored);
      const now = Date.now();
      // Filter out expired attempts
      return parsed.attempts.filter((timestamp) => now - timestamp < windowMs);
    } catch {
      return [];
    }
  }, [storageKey, windowMs]);

  const saveAttempts = useCallback(
    (attempts: number[]) => {
      try {
        const data: StoredAttempts = { attempts };
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch {
        // localStorage might be full or disabled
      }
    },
    [storageKey]
  );

  const calculateState = useCallback((): RateLimitState => {
    const attempts = getStoredAttempts();
    const isLimited = attempts.length >= maxAttempts;
    const oldestAttempt = attempts.length > 0 ? Math.min(...attempts) : null;
    const resetTime = oldestAttempt ? oldestAttempt + windowMs : null;
    const timeUntilReset = resetTime ? Math.max(0, resetTime - Date.now()) : 0;

    return {
      isLimited,
      remainingAttempts: Math.max(0, maxAttempts - attempts.length),
      resetTime,
      timeUntilReset,
    };
  }, [getStoredAttempts, maxAttempts, windowMs]);

  const [state, setState] = useState<RateLimitState>(calculateState);

  const checkLimit = useCallback((): boolean => {
    const newState = calculateState();
    setState(newState);
    return !newState.isLimited;
  }, [calculateState]);

  const recordAttempt = useCallback(() => {
    const attempts = getStoredAttempts();
    attempts.push(Date.now());
    saveAttempts(attempts);
    setState(calculateState());
  }, [getStoredAttempts, saveAttempts, calculateState]);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore errors
    }
    setState(calculateState());
  }, [storageKey, calculateState]);

  return {
    ...state,
    checkLimit,
    recordAttempt,
    reset,
  };
}

export function formatTimeRemaining(ms: number): string {
  const seconds = Math.ceil(ms / 1000);
  if (seconds < 60) return `${seconds} second${seconds !== 1 ? "s" : ""}`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  const hours = Math.ceil(minutes / 60);
  return `${hours} hour${hours !== 1 ? "s" : ""}`;
}
